import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Flex, Heading, Label, Text, TextArea } from 'pcln-design-system';
import { ThumbsUp } from 'pcln-icons';
import styled from 'styled-components';
const StyledScoreButton = /*#__PURE__*/styled(Button).withConfig({
  displayName: "CaresSurvey__StyledScoreButton",
  componentId: "sc-15blmi6-0"
})(["&:hover{decoration:none;}"]);
const QuestionBoxWrapper = /*#__PURE__*/styled(Box).withConfig({
  displayName: "CaresSurvey__QuestionBoxWrapper",
  componentId: "sc-15blmi6-1"
})(["margin-bottom:32px;&:last-child{margin-bottom:16px;}"]);
const TextQuestion = ({
  question,
  scoresSubmitted,
  setScoresSubmitted
}) => {
  const [feedbackTextAreaContent, setFeedbackTextAreaContent] = useState('');
  function handleTextChange(e) {
    setFeedbackTextAreaContent(e.target.value);
    setScoresSubmitted({
      ...scoresSubmitted,
      [question.question_id]: {
        type: 'text',
        value: e.target.value
      }
    });
  }
  return React.createElement(React.Fragment, null, React.createElement(Label, {
    hidden: true,
    htmlFor: 'feedback-textarea'
  }, question.textAreaLabel ?? 'feedback textarea'), React.createElement(TextArea, {
    id: 'feedback-textarea',
    name: 'feedback-textarea',
    placeholder: question.textAreaPlaceholder ?? '',
    rows: 5,
    value: feedbackTextAreaContent,
    onChange: handleTextChange
  }));
};
const RatingQuestion = ({
  question,
  scoresSubmitted,
  setScoresSubmitted
}) => {
  return React.createElement(React.Fragment, null, React.createElement(Flex, {
    width: 1
  }, [...new Array(5)].map((value, index) => {
    function handleScoreClick(e) {
      e.preventDefault();
      setScoresSubmitted({
        ...scoresSubmitted,
        [question.question_id]: {
          type: 'rating',
          value: index + 1
        }
      });
    }
    return React.createElement(StyledScoreButton, {
      key: index,
      mr: 2,
      width: '40px',
      py: '9px',
      px: '14px',
      color: 'primary',
      variation: scoresSubmitted[question.question_id]?.value === index + 1 ? 'fill' : 'outline',
      onClick: handleScoreClick
    }, index + 1);
  })), React.createElement(Flex, {
    mt: 2
  }, React.createElement(Text, {
    fontSize: '10px',
    fontWeight: 'bold',
    mr: '77px',
    width: '77px'
  }, question?.notLikelyText ?? question?.minLabel), React.createElement(Text, {
    fontSize: '10px',
    fontWeight: 'bold',
    textAlign: 'right',
    width: '77px'
  }, question?.mostLikelyText ?? question?.maxLabel)));
};
/**
 * @public
 */
export const caresSurveyProps = {
  submitFunction: PropTypes.Func,
  surveyDetails: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    totalPages: PropTypes.number,
    answered: PropTypes.bool,
    pages: PropTypes.arrayOf(PropTypes.shape({
      pageNumber: PropTypes.number,
      sections: PropTypes.arrayOf(PropTypes.shape({
        questions: PropTypes.arrayOf(PropTypes.shape({
          question_id: PropTypes.string,
          type: PropTypes.string,
          title: PropTypes.string,
          maxValue: PropTypes.number,
          minLabel: PropTypes.string,
          maxLabel: PropTypes.string
        }))
      }))
    }))
  })
};
/**
 * @public
 */
const CaresSurvey = props => {
  const {
    surveyDetails,
    submitFunction
  } = props;
  const [currentPage, setCurrentPage] = useState(0);
  const [scoresSubmitted, setScoresSubmitted] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const {
    title,
    totalPages,
    pages,
    answered,
    description
  } = surveyDetails;
  const waitForTextSubmit = !answered && pages[currentPage].sections.some(section => section.questions.some(question => question.type === 'text'));
  const isAllQuestionsAnswered = !answered && pages[currentPage].sections.every(section => section.questions.every(question => scoresSubmitted[question.question_id]));
  const isLastPage = !answered && currentPage === totalPages - 1;
  function handleSubmit(e) {
    e.preventDefault();
    if (isLastPage) {
      submitFunction(scoresSubmitted);
      setIsSubmitted(true);
    }
  }
  function setNextPage() {
    setCurrentPage(currentPage + 1);
  }
  useEffect(function () {
    if (!waitForTextSubmit && isAllQuestionsAnswered && !isLastPage) {
      setCurrentPage(currentPage + 1);
    }
  }, [waitForTextSubmit, isAllQuestionsAnswered, isLastPage, currentPage]);
  return React.createElement(React.Fragment, null, isSubmitted ? React.createElement(Flex, {
    justifyContent: 'center',
    my: 4
  }, React.createElement(ThumbsUp, {
    color: 'green'
  }), React.createElement(Text, {
    color: 'green',
    ml: 2,
    mt: '2px'
  }, "Thank you for your feedback!")) : !answered ? React.createElement(React.Fragment, null, React.createElement(Heading, {
    fontSize: 3,
    mb: 2,
    mt: 0,
    fontWeight: 'bold'
  }, title), description ? React.createElement(Text, {
    fontSize: 1,
    my: 3
  }, description) : null, React.createElement(Flex, {
    flexDirection: 'column'
  }, React.createElement("form", {
    onSubmit: handleSubmit
  }, pages[currentPage].sections.map((section, sectionIdx) => {
    const {
      questions
    } = section;
    return React.createElement("section", {
      key: `survey-section${sectionIdx}`
    }, questions && questions.map(question => {
      return React.createElement(QuestionBoxWrapper, {
        key: question.question_id
      }, React.createElement(Text, {
        fontSize: 1,
        mb: 3
      }, question.title), React.createElement(Flex, {
        flexDirection: 'column'
      }, question.type === 'rating' && React.createElement(RatingQuestion, {
        question: question,
        scoresSubmitted: scoresSubmitted,
        setScoresSubmitted: setScoresSubmitted
      }), question.type === 'text' && React.createElement(TextQuestion, {
        question: question,
        scoresSubmitted: scoresSubmitted,
        setScoresSubmitted: setScoresSubmitted
      })));
    }));
  }), !isLastPage && waitForTextSubmit && React.createElement(Flex, {
    justifyContent: 'flex-end'
  }, React.createElement(Button, {
    onClick: setNextPage,
    width: [1, '146px']
  }, "Next")), isLastPage && React.createElement(Flex, {
    justifyContent: 'flex-end'
  }, React.createElement(Button, {
    type: 'submit',
    width: [1, '146px']
  }, "Submit"))))) : null);
};
CaresSurvey.defaultProps = {
  surveyDetails: {
    title: 'We want to hear from you!',
    description: 'Your feedback is important and will be used to make improvements over time. For any specific or immediate needs about a current or upcoming trip, please visit our Help Center for next steps.',
    answered: false,
    totalPages: 2,
    pages: [{
      pageNumber: 1,
      sections: [{
        questions: [{
          question_id: 'e65ae707-9219-46b0-8608-da02685dc505',
          title: 'Overall, how satisfied were you with your recent booking experience with Priceline?',
          type: 'rating',
          maxValue: 5,
          minLabel: 'Not Satisfied',
          maxLabel: 'Most Satisfied'
        }, {
          question_id: '3e186900-93c3-4512-aa66-43acee96f181',
          title: 'How much do you agree with the following statement?: "I feel that I got a great bargain booking with Priceline instead of another provider today."',
          type: 'rating',
          maxValue: 5,
          minLabel: 'Completely Disagree',
          maxLabel: 'Completely Agree'
        }]
      }]
    }, {
      pageNumber: 2,
      sections: [{
        questions: [{
          question_id: '9591ba4f-7540-4aea-8fe6-3fb7f18a043e',
          title: "We'd love your honest feedback. Is there anything we can do to improve your next booking experience?",
          type: 'text'
        }]
      }]
    }]
  }
};
export default CaresSurvey;